// ExpectedDocuments - the bottom of the Processing tab (owner, 2026-09-19):
// "list the possible missing data formats and names (schedule, architectural
// plan, electrical level 5, plumbing level 2...) in a table, with a status
// showing whether they were uploaded or not, found in document control or not".
//
// Rows are levels (then the project-wide items), columns are disciplines.
// A cell is found (which documents - each opens by version), missing, or not
// applicable: obvious ones (no ceiling plan on a roof) or a person's mark,
// which the owner side can set and undo. Above it: ready to generate or not,
// what is missing to start, and the "Process automatically" switch the
// nightly readiness sweep honours. GET /projects/:id/glance/expected.
import React, { useCallback, useEffect, useState } from "react";
import { Button, Popover, Switch, message } from "antd";
import { url, tokenRef } from "api";
import VersionChip from "../../../Documents/VersionChip";

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem(tokenRef)}` });
const WORD = { found: "Filed", missing: "Missing", na: "n/a", none: "" };

export function readyText(r) {
  if (!r) return "";
  if (r.ready) return `Ready to generate: yes${r.grid === "likely" ? ` (${r.gridWhy})` : ""}.`;
  return `Ready to generate: no. Missing to start: ${r.missing.join("; ")}.${r.gridWhy ? ` (${r.gridWhy})` : ""}`;
}

export function sweepText(s) {
  if (!s || !s.at) return "";
  const when = new Date(s.at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  return `Last automatic check ${when}: ${s.reason || (s.ready ? "ready" : "not ready")}.`;
}

function DocList({ docs, more }) {
  return (
    <ul className="g-exp-docs">
      {docs.map((d) => (
        <li key={d.id}><span>{d.name || "Untitled"}</span> <VersionChip docId={d.id} rev={d.rev} /></li>
      ))}
      {more > 0 && <li className="g-tsub">and {more} more in Document Control</li>}
    </ul>
  );
}

function Cell({ cell, rowLabel, colLabel, canEdit, onMark }) {
  const body = (
    <div className="g-exp-pop">
      <b>{rowLabel}: {colLabel}</b>
      {cell.status === "found" && <DocList docs={cell.docs} more={cell.more} />}
      {cell.status === "missing" && <p className="g-tsub">Nothing filed in Document Control for this yet.</p>}
      {cell.status === "na" && (
        <p className="g-tsub">
          Not applicable{cell.mark ? `, marked by ${cell.mark.byName || "a project owner"}` : " for this row"}.
        </p>
      )}
      {cell.detail && <p className="g-tsub">{cell.detail}</p>}
      {canEdit && cell.status === "missing" && <Button size="small" onClick={() => onMark(cell.key, "na")}>Mark not applicable</Button>}
      {canEdit && cell.status === "na" && <Button size="small" onClick={() => onMark(cell.key, cell.mark ? null : "expected")}>{cell.mark ? "Undo" : "Expect it here"}</Button>}
      {canEdit && cell.status === "missing" && cell.mark && cell.mark.state === "expected" && <Button size="small" type="link" onClick={() => onMark(cell.key, null)}>Undo</Button>}
    </div>
  );
  if (cell.status === "none") return <td className="g-exp g-exp-none" />;
  return (
    <td className={`g-exp g-exp-${cell.status}`}>
      <Popover content={body} trigger={["hover", "click"]} placement="top">
        <button type="button" className="g-exp-btn" aria-label={`${rowLabel} ${colLabel}: ${WORD[cell.status]}`}>
          {cell.status === "found" ? (cell.docs.length ? `✓ ${cell.docs.length + (cell.more || 0)}` : "✓") : WORD[cell.status]}
        </button>
      </Popover>
    </td>
  );
}

export default function ExpectedDocuments({ projectId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(() => {
    fetch(`${url}/projects/${projectId}/glance/expected`, { headers: auth() })
      .then(async (r) => { const b = await r.json().catch(() => ({})); if (!r.ok) throw new Error(b.err || `HTTP ${r.status}`); return b; })
      .then((b) => { setData(b); setError(""); })
      .catch((e) => setError(e.message));
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const put = async (path, body, done) => {
    try {
      const r = await fetch(`${url}/projects/${projectId}/glance/expected/${path}`, { method: "PUT", headers: { "Content-Type": "application/json", ...auth() }, body: JSON.stringify(body) });
      const b = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(b.err || `HTTP ${r.status}`);
      if (done) message.success(done);
      load();
    } catch (e) { message.error(e.message); }
  };
  const onMark = (key, state) => put("mark", { key, state }, state === "na" ? "Marked not applicable" : state === "expected" ? "Now expected" : "Mark removed");

  if (error && !data) return <p className="g-note">Expected documents: {error}</p>;
  if (!data) return null;
  // A 200 WHOSE BODY IS NOT THE CHECKLIST IS NOT AN EMPTY CHECKLIST. An older server, a proxy answering for the
  // route, a partial body: `data` is truthy, `data.readiness.ready` throws during render, and an uncaught render
  // error unmounts the WHOLE documentation page - not this panel. That is how the Processing tab came back as an
  // empty <body> with no message anywhere. Every field read unconditionally below is checked here.
  const shaped = data.readiness && data.missing && data.other && Array.isArray(data.columns)
    && Array.isArray(data.levels) && Array.isArray(data.items) && Array.isArray(data.drawings);
  if (!shaped) return <p className="g-note">Expected documents: the checklist did not come back from the server.</p>;
  const cols = data.columns;
  return (
    <section className="g-exp-section" aria-label="Expected documents">
      <div className="g-viewhead">
        <div>
          <h3>Expected documents</h3>
          <p className={`g-note ${data.readiness.ready ? "" : "g-exp-notready"}`}>{readyText(data.readiness)}</p>
          <p className="g-tsub">
            {data.documents} document{data.documents === 1 ? "" : "s"} in Document Control
            {data.processed ? `; ${data.newDocs ? `${data.newDocs} not in the building data yet` : "all in the building data"}` : "; not processed yet"}.
            {" "}{data.missing.count ? `${data.missing.count} plan${data.missing.count === 1 ? "" : "s"} missing` : "No plan missing"} for the levels found.
          </p>
          {data.lastSweep && <p className="g-tsub">{sweepText(data.lastSweep)}</p>}
        </div>
        {data.canEdit && (
          <label className="g-exp-auto">
            <Switch size="small" checked={data.autoProcess} onChange={(on) => put("auto", { on }, on ? "Processed automatically when ready" : "Automatic processing off")} />
            {" "}Process automatically
          </label>
        )}
      </div>
      <div className="g-exp-wrap">
        <table className="g-exp-table">
          <thead>
            <tr><th scope="col">Level</th>{cols.map((c) => <th key={c.key} scope="col">{c.label}</th>)}</tr>
          </thead>
          <tbody>
            {data.levels.map((row) => (
              <tr key={row.id || "unplaced"} className={row.unplaced ? "g-exp-unplaced" : ""}>
                <th scope="row">{row.label}</th>
                {row.cells.map((cell, i) => <Cell key={cell.key} cell={cell} rowLabel={row.label} colLabel={cols[i].label} canEdit={data.canEdit && !row.unplaced} onMark={onMark} />)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="g-exp-wrap">
        <table className="g-exp-table g-exp-items">
          <thead><tr><th scope="col">Project-wide</th><th scope="col">Status</th></tr></thead>
          <tbody>
            {data.items.map((it) => (
              <tr key={it.id}>
                <th scope="row">{it.label}</th>
                <Cell cell={it} rowLabel={it.label} colLabel="whole project" canEdit={data.canEdit} onMark={onMark} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="g-tsub">
        <span className="g-exp-key g-exp-found">✓ Filed</span> <span className="g-exp-key g-exp-missing">Missing</span> <span className="g-exp-key g-exp-na">n/a</span>
        {" "}Plans only: {data.drawings.length ? `also filed, not plans: ${data.drawings.map((d) => `${d.count} ${d.label.toLowerCase()}`).join(", ")} (elevations, sections, details, schedules)` : "no other drawings"}
        {data.other.count ? `; ${data.other.count} document${data.other.count === 1 ? "" : "s"} not tied to a discipline` : ""}.
        {" "}Put the level in the file or sheet name so a plan lands on its row.
      </p>
    </section>
  );
}
